import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import axios from "axios";
import { useLanguage } from "./LanguageContext";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ===================== Palette / tokens ===================== */
const ui = {
  bg: "#ffffff",
  card: "#fafafa",
  cardAlt: "#f5f5f7",
  text: "#0f0f0f",
  dim: "#6b6b6b",
  border: "rgba(0,0,0,0.08)",
  accent: "#111827",        // brand dark
  accentSoft: "rgba(17,24,39,0.06)",
  star: "#ffb400",          // rating accent
  danger: "#b91c1c",
  success: "#16a34a"
};

const radius = {
  md: "12px",
  lg: "16px",
  pill: "999px"
};

const shadow = {
  card: "0 6px 24px rgba(0,0,0,0.08)",
  hover: "0 10px 30px rgba(0,0,0,0.12)"
};

/* ===================== Animations ===================== */
const spin = keyframes`
  0%   { transform: rotate(0deg);   }
  100% { transform: rotate(360deg); }
`;

const slideFade = keyframes`
  0% { opacity: 0; transform: translateY(-6px); }
  100% { opacity: 1; transform: translateY(0); }
`;

/* ===================== Styled Components ===================== */
const ReviewContainer = styled.div`
  margin-top: 2rem;
  border-top: 1px solid ${ui.border};
  width: 100%;
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 16px 10px 8px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const AddReviewButton = styled.button`
  justify-self: start;
  background: ${ui.accent};
  color: #fff;
  padding: 10px 16px;
  border: 1px solid ${ui.accent};
  cursor: pointer;
  border-radius: ${radius.md};
  font-weight: 600;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, opacity 0.2s ease;

  &:hover { transform: translateY(-1px); box-shadow: ${shadow.card}; }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid ${ui.accent}; outline-offset: 2px; }
`;

const AverageRatingWrap = styled.div`
  justify-self: end;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${ui.card};
  border: 1px solid ${ui.border};
  padding: 8px 12px;
  border-radius: ${radius.pill};
`;

const AverageRatingText = styled.span`
  font-size: 0.95rem;
  color: ${ui.dim};
`;

const AverageStars = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 1.1rem;
  color: ${ui.star};
  letter-spacing: 1px;
`;

const BadgeNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  padding: 2px 8px;
  background: ${ui.accentSoft};
  color: ${ui.accent};
  border-radius: ${radius.pill};
  font-weight: 700;
  font-size: 0.9rem;
`;

/* --- Collapsible form --- */
const ReviewForm = styled.div`
  background: ${ui.cardAlt};
  border: 1px solid ${ui.border};
  padding: ${({ isOpen }) => (isOpen ? "14px" : "0 14px")};
  border-radius: ${radius.lg};
  overflow: hidden;
  max-height: ${({ isOpen }) => (isOpen ? "820px" : "0")};
  opacity: ${({ isOpen }) => (isOpen ? "1" : "0")};
  transition: max-height 0.5s ease-in-out, opacity 0.3s ease-in-out, padding 0.35s ease-in-out;
  margin: 12px 10px 6px;
  box-shadow: ${({ isOpen }) => (isOpen ? shadow.card : "none")};
  will-change: max-height, opacity, padding;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Label = styled.div`
  margin-top: 6px;
  font-size: 0.92rem;
  color: ${ui.dim};
  font-weight: 600;
`;

const ReviewInput = styled.textarea`
  width: 100%;
  max-width: 100%;              /* ✅ არ გადავიდეს საზღვრებს გარეთ */
  box-sizing: border-box;       /* ✅ padding + border ჩაჯდეს 100%-ში */
  height: 100px;
  min-height: 80px;
  margin-top: 8px;
  padding: 10px 12px;
  font-size: 0.98rem;
  color: ${ui.text};
  background: #fff;
  border-radius: ${radius.md};
  border: 1px solid ${ui.border};
  resize: vertical;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: ${ui.accent};
    box-shadow: 0 0 0 4px ${ui.accentSoft};
    outline: none;
  }

  /* ✅ მობილურისთვის ოდნავ უფრო კომპაქტური */
  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 0.95rem;
    height: 90px;
    min-height: 74px;
  }
`;

const Stars = styled.div`
  display: inline-flex;
  gap: 6px;
  color: ${ui.star};
  font-size: 1.3rem;
  cursor: pointer;
  padding: 6px 0;

  span {
    display: inline-flex;
    line-height: 1;
    transition: transform 0.12s ease, filter 0.12s ease;
    user-select: none;

    &:hover { transform: scale(1.08); filter: brightness(1.05); }
  }
`;

const ReviewButton = styled.button`
  background: ${ui.accent};
  color: #fff;
  padding: 10px 16px;
  border: 1px solid ${ui.accent};
  cursor: pointer;
  border-radius: ${radius.md};
  margin-top: 10px;
  font-weight: 600;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  margin-left: 16px;

  &:hover { transform: translateY(-1px); box-shadow: ${shadow.card}; }
  &:active { transform: translateY(0); }
  &:focus-visible { outline: 2px solid ${ui.accent}; outline-offset: 2px; }
`;

const ReviewList = styled.ul`
  list-style: none;
  padding: 0 6px 6px;
  margin: 8px 0 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 700px) {
    gap: 14px;
  }
`;

const ReviewItem = styled.li`
  background: ${ui.bg};
  border: 1px solid ${ui.border};
  padding: 14px;
  border-radius: ${radius.lg};
  box-shadow: ${shadow.card};
  animation: ${slideFade} 200ms ease-out;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 10px;
  animation: ${({$animate}) => $animate ? css`${slideFade} 200ms ease-out` : "none"};

  &:hover { box-shadow: ${shadow.hover}; }

  /* left accent bar */
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    border-top-left-radius: ${radius.lg};
    border-bottom-left-radius: ${radius.lg};
    background: linear-gradient(180deg, ${ui.accent} 0%, #2f3b53 100%);
    opacity: 0.9;
  }
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Avatar = styled.div`
  width: 36px; height: 36px;
  border-radius: 50%;
  background: ${ui.accentSoft};
  color: ${ui.accent};
  font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid ${ui.border};
  flex: 0 0 auto;
`;

const Username = styled.strong`
  font-size: 0.98rem;
  color: ${ui.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InlineStars = styled(Stars)`
  font-size: 1.05rem;
  gap: 3px;
  margin-left: auto;
  color: ${ui.star};
  cursor: default;
  span { pointer-events: none; transform: none !important; }
`;

const ItemBody = styled.div`
  grid-column: 1 / -1;
  margin-top: 6px;
  color: ${ui.text};
  font-size: 0.97rem;
  line-height: 1.55;
  word-break: break-word;
`;

const ActionButtons = styled.div`
  display: inline-flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;

  ${ReviewButton} {
    padding: 8px 12px;
    font-size: 0.9rem;
  }

  .ghost {
    background: #fff;
    color: ${ui.accent};
    border-color: ${ui.border};
  }
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 18px auto 26px;
  padding: 10px 18px;
  font-size: 0.98rem;
  background: ${ui.bg};
  color: ${ui.accent};
  border: 1px solid ${ui.border};
  border-radius: ${radius.pill};
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

  &:hover { transform: translateY(-1px); box-shadow: ${shadow.card}; background: #fff; }
  &:active { transform: translateY(0); }
`;

const Loader = styled.div`
  display: grid;
  place-items: center;
  padding: 40px 0;
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.08);
  border-top: 4px solid ${ui.accent};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
`;

const EmptyState = styled.div`
  display: grid;
  place-items: center;
  text-align: center;
  padding: 40px 10px 52px;
  color: ${ui.dim};
  font-size: 0.98rem;
  border: 1px dashed ${ui.border};
  border-radius: ${radius.lg};
  margin: 10px;
  background: #fff;
`;

// === Auth Modal (same style as FavoritesContext) ===
const AuthOverlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(17,24,39,.5);
  backdrop-filter: blur(1.5px);
  display: grid; place-items: center;
  z-index: 3000;
`;
const AuthModal = styled.div`
  width: min(420px, 92vw);
  background: #ffffff;
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,.18);
  overflow: hidden;
`;
const AuthHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
`;
const AuthTitle = styled.h3`
  margin: 0; font-size: 16px; font-weight: 900; color: #111827;
`;
const AuthCloseBtn = styled.button`
  width: 34px; height: 34px;
  border-radius: 10px; border: 1px solid rgba(0,0,0,.08);
  background: #fff; display: grid; place-items: center; cursor: pointer;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 30px rgba(0,0,0,.08); }
`;
const AuthBody = styled.div`
  padding: 14px 16px; color: #111827; font-size: 14px;
`;
const AuthActions = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
  padding: 0 16px 16px;
`;
const AuthBtn = styled.a`
  flex: 1 1 140px; height: 40px;
  border-radius: 10px; border: 1px solid rgba(0,0,0,.08);
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 14px; text-decoration: none; cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
  background: #fff; color: #111827;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.08); }
`;
const AuthBtnPrimary = styled(AuthBtn)`
  background: #111827; color: #fff; border-color: #111827;
`;



/* ===================== Component ===================== */
const Reviews = ({ perfumeId, userId }) => {
  const { t, language } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [reviewText, setReviewText] = useState("");
  const [stars, setStars] = useState(5);
  const [isEditing, setIsEditing] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const [user, setUser] = useState(null);
  const [visibleReviews, setVisibleReviews] = useState(5);
  const [loadingReviews, setLoadingReviews] = useState(true);
const [showAuthModal, setShowAuthModal] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);   // იყო თუ არა პირველი ჩატვირთვა დასრულებული
const [isRefreshing, setIsRefreshing] = useState(false);   // ჩუმი განახლება (UI არ იცვლება)

useEffect(() => {
  if (!showAuthModal) return;
  const b = document.body;
  const prev = b.style.overflow;
  b.style.overflow = 'hidden';
  return () => { b.style.overflow = prev; };
}, [showAuthModal]);

  useEffect(() => {
    const fetchUserAndReviews = () => {
      const storedUser = localStorage.getItem("user");
      let parsedUser = null;

      if (storedUser) {
        try {
          parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
        } catch (error) {
          console.error("Error parsing user JSON:", error);
          setUser(null);
        }
      } else {
        setUser(null);
      }

    fetchReviews(parsedUser);
    };

    fetchUserAndReviews();

    const handleStorageChange = () => fetchUserAndReviews();
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const fetchUser = () => {
    const storedUser = localStorage.getItem("user");
    if (storedUser && storedUser !== "undefined") {
      try { setUser(JSON.parse(storedUser)); }
      catch (e) { console.error("Error parsing user JSON:", e); setUser(null); }
    } else {
      setUser(null);
    }
  };

  useEffect(() => {
    fetchUser();
    window.addEventListener("storage", fetchUser);
    return () => window.removeEventListener("storage", fetchUser);
  }, []);

 useEffect(() => {
   // თუ უკვე გვაქვს მონაცემი, განვაახლოთ ჩუმად; წინააღმდეგ შემთხვევაში – სრულად
   fetchReviews(user, { silent: bootstrapped });
 }, [user, perfumeId, language]);

 useEffect(() => { if (user) { fetchReviews(user, { silent: bootstrapped }); } }, [user]);


 const fetchReviews = (currentUser, { silent = false } = {}) => {
   if (!bootstrapped && !silent) {
     setLoadingReviews(true);     // მხოლოდ პირველ ჩატვირთვაზე აჩვენე Loader
   } else {
     setIsRefreshing(true);       // სხვაგან – ჩუმი განახლება
   }
    api
      .get(`${API_BASE_URL}/get_reviews.php?product_id=${perfumeId}`)
      .then((res) => {
        if (Array.isArray(res.data)) {
          const formattedReviews = res.data.map((review) => ({
            ...review,
            username:
              review.user_id === currentUser?.id
                ? (language === "en" ? "You" : "შენ")
                : review.username,
          }));

          setReviews(formattedReviews);
          calculateAverageRating(formattedReviews);
        } else {
          setReviews([]);
        }
       setLoadingReviews(false);
      setIsRefreshing(false);
       setBootstrapped(true);
      })
    .catch((error) => {
      console.error("Error fetching reviews:", error);
      setReviews([]);
       setLoadingReviews(false);
       setIsRefreshing(false);
    });
  };

  const calculateAverageRating = (reviews) => {
    if (reviews.length === 0) { setAverageRating(0); return; }
    const totalStars = reviews.reduce((acc, r) => acc + r.stars, 0);
    const avg = totalStars / reviews.length;
    setAverageRating(avg.toFixed(1));
  };

  const submitReview = () => {
    if (!reviewText.trim()) return;

    const reviewData = {
      product_id: perfumeId,
      user_id: user?.id,
      stars,
      review_text: reviewText,
    };

    api
      .post(`${API_BASE_URL}/add_review.php`, reviewData, {
        headers: { "Content-Type": "application/json" },
      })
      .then((res) => {
        if (res.data.error) { console.error("❌ Backend Error:", res.data.error); return; }
        const newReviews = [{
          ...reviewData,
          id: res.data.review_id,
          username: language === "en" ? "You" : "შენ",
          user_id: user.id
        }, ...reviews];
        setReviews(newReviews);
        calculateAverageRating(newReviews);
        setReviewText("");
        setShowReviewForm(false);
        fetchReviews(user, { silent: true });
      })
      .catch((error) => console.error("❌ Error submitting review:", error));
  };

  const deleteReview = (reviewId) => {
    if (!user?.id) { alert("You must be logged in to delete reviews."); return; }
    const confirmDelete = window.confirm(t("review.confirmDelete"));
    if (!confirmDelete) return;

    api.post(
      `${API_BASE_URL}/delete_review.php`,
      { review_id: reviewId, user_id: user.id },
      { headers: { "Content-Type": "application/json" } }
    )
    .then((res) => {
      if (res.data.error) { console.error("❌ Error deleting review:", res.data.error); return; }
      const newReviews = reviews.filter((r) => r.id !== reviewId);
 setReviews(newReviews);            // ოპტიმისტური UI
 calculateAverageRating(newReviews);
 fetchReviews(user, { silent: true });
    })
    .catch((error) => console.error("❌ Error deleting review:", error));
  };

  const updateReview = (reviewId) => {
    if (!user?.id) { alert("You must be logged in to edit reviews."); return; }
    if (!reviewText.trim()) { alert("Review text cannot be empty."); return; }

    api.post(
      `${API_BASE_URL}/update_review.php`,
      { review_id: reviewId, user_id: user.id, stars, review_text: reviewText },
      { headers: { "Content-Type": "application/json" } }
    )
    .then((res) => {
      if (res.data.error) { console.error("❌ Error updating review:", res.data.error); return; }
      const updatedReviews = reviews.map((r) =>
        r.id === reviewId ? { ...r, review_text: reviewText, stars, user_id: user.id } : r
      );
 setReviews(updatedReviews);        // ოპტიმისტური UI
 calculateAverageRating(updatedReviews);
 setIsEditing(null);
 fetchReviews(user, { silent: true });
    })
    .catch((error) => console.error("❌ Error updating review:", error));
  };

  return (
    <ReviewContainer>
      <Header>
        <AddReviewButton onClick={() => user ? setShowReviewForm(!showReviewForm) : setShowAuthModal(true)}>
          {showReviewForm ? t("review.cancel") : t("review.addNew")}
        </AddReviewButton>

        <AverageRatingWrap>
          <AverageRatingText>{t("review.averageRating")}</AverageRatingText>
          <AverageStars>
            {"★".repeat(Math.round(averageRating)) + "☆".repeat(5 - Math.round(averageRating))}
          </AverageStars>
          <BadgeNumber>{averageRating || "0.0"}</BadgeNumber>
        </AverageRatingWrap>
      </Header>

      <ReviewForm isOpen={showReviewForm}>
        <Label>{t("review.writereview")}:</Label>
        <ReviewInput
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          placeholder={t("review.placeholder")}
        />
        <Label>{t("review.score")}:</Label>
        <Stars>
          {[...Array(5)].map((_, i) => (
            <span key={i} onClick={() => setStars(i + 1)}>
              {i < stars ? "★" : "☆"}
            </span>
          ))}
        </Stars>
        <ReviewButton onClick={submitReview}>{t("review.submit")}</ReviewButton>
      </ReviewForm>

        {loadingReviews ? (
          <Loader><Spinner /></Loader>
        ) : reviews.length === 0 ? (
          <EmptyState>
            {language?.startsWith("ka")
              ? "კომენტარები ვერ მოიძებნა."
              : "No reviews found."}
          </EmptyState>
        ) : (
        <>
          <ReviewList>
              {reviews.slice(0, visibleReviews).map((review) => (
                <ReviewItem
                  key={review.id ?? `${review.user_id}-${review.created_at ?? Math.random()}`}
                  $animate={!isRefreshing}
                >
                <ItemHead>
                  <Avatar>{(review.username || "?").slice(0,1).toUpperCase()}</Avatar>
                  <Username title={review.username}>{review.username}</Username>
                  <InlineStars>
                    {"★".repeat(review.stars) + "☆".repeat(5 - review.stars)}
                  </InlineStars>
                </ItemHead>

                <ItemBody>
                  <p>{review.review_text}</p>

                  {String(review.user_id) === String(user?.id) && (
                    <ActionButtons>
                      {isEditing === review.id ? (
                        <>
                          <ReviewInput
                            value={reviewText}
                            onChange={(e) => setReviewText(e.target.value)}
                            placeholder={t("review.placeholder")}
                          />
                          <ReviewButton onClick={() => updateReview(review.id)}>
                            {t("review.update")}
                          </ReviewButton>
                          <ReviewButton className="ghost" onClick={() => setIsEditing(null)}>
                            {t("review.cancel")}
                          </ReviewButton>
                        </>
                      ) : (
                        <>
                          <ReviewButton onClick={() => setIsEditing(review.id)}>
                            {t("review.edit")}
                          </ReviewButton>
                          <ReviewButton className="ghost" onClick={() => deleteReview(review.id)}>
                            {t("review.delete")}
                          </ReviewButton>
                        </>
                      )}
                    </ActionButtons>
                  )}
                </ItemBody>
              </ReviewItem>
            ))}
          </ReviewList>

          {visibleReviews < reviews.length && (
            <LoadMoreButton onClick={() => setVisibleReviews(visibleReviews + 5)}>
              {t("purchases.loadmore")}
            </LoadMoreButton>
          )}
        </>
      )}
      {showAuthModal && (
  <AuthOverlay onClick={() => setShowAuthModal(false)} role="dialog" aria-modal="true">
    <AuthModal onClick={(e) => e.stopPropagation()}>
      <AuthHeader>
        <AuthTitle>
          {language === "ka" ? "ავტორიზაციაა საჭირო" : "Authorization required"}
        </AuthTitle>
        <AuthCloseBtn onClick={() => setShowAuthModal(false)} aria-label="Close">×</AuthCloseBtn>
      </AuthHeader>
      <AuthBody>
        {language === "ka"
          ? "მიმოხილვის დასაწერად გთხოვთ გაიაროთ ავტორიზაცია."
          : "Please log in to write a review."}
      </AuthBody>
      <AuthActions>
        <AuthBtnPrimary href="/login">
          {language === "ka" ? "შესვლა" : "Log in"}
        </AuthBtnPrimary>
        <AuthBtn as="button" onClick={() => setShowAuthModal(false)}>
          {language === "ka" ? "დახურვა" : "Close"}
        </AuthBtn>
      </AuthActions>
    </AuthModal>
  </AuthOverlay>
)}

    </ReviewContainer>
  );
};

export default Reviews;
